import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, CheckCircle, Clock, XCircle, Wifi, Radio, Server } from 'lucide-react';
import {
  EditModeProvider,
  EditModeContext,
  AdminToolbar,
  EditableText
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_OPTIONS = [
  { value: 'wisp', label: 'Wireless Internet Service Provider (WiSP)' },
  { value: 'telecom', label: 'Telecommunications' },
  { value: 'it_msp', label: 'IT / Managed Service Provider' },
  { value: 'electrical', label: 'Electrical Contractor' },
  { value: 'solar', label: 'Solar Installer' },
  { value: 'security', label: 'Security / CCTV Installer' },
  { value: 'network', label: 'Network Integrator' },
  { value: 'other', label: 'Other (please specify in notes)' },
];

export default function TradeApplicationPage() {
  return (
    <EditModeProvider pageSlug="trade-application">
      <TradeApplicationPageContent />
    </EditModeProvider>
  );
}

function TradeApplicationPageContent() {
  const { pageContent, setPageContent } = useContext(EditModeContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existingApp, setExistingApp] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(!!user);

  const [form, setForm] = useState({
    business_name: '',
    abn: '',
    contact_name: user ? `${user.first_name} ${user.last_name}` : '',
    email: user?.email || '',
    phone: user?.phone || '',
    industry: '',
    billing_address: '',
    shipping_address: '',
    website: '',
    notes: '',
  });

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        contact_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || f.contact_name,
        email: user.email || f.email,
        phone: user.phone || f.phone,
      }));
      checkExistingApplication();
    } else {
      setCheckingStatus(false);
    }
  }, [user]);

  const checkExistingApplication = async () => {
    try {
      const res = await axios.get(`${API}/trade-application/status`);
      setExistingApp(res.data);
    } catch (err) {}
    finally { setCheckingStatus(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in or register to submit your trade application');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/trade-application`, form);
      toast.success('Trade application submitted successfully');
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally { setLoading(false); }
  };

  if (checkingStatus) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-zinc-800 rounded w-1/2 mx-auto mb-4" />
            <div className="h-4 bg-zinc-800 rounded w-2/3 mx-auto" />
          </div>
        </div>
      </Layout>
    );
  }

  if (existingApp) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16" data-testid="trade-application-status">
          <div className="bg-card border border-border rounded-sm p-8 text-center">
            {existingApp.status === 'pending' && (
              <>
                <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mb-2">Application Pending</h1>
                <p className="text-muted-foreground">Your trade application is currently under review. We'll notify you by email once a decision has been made.</p>
              </>
            )}
            {existingApp.status === 'approved' && (
              <>
                <CheckCircle className="h-16 w-16 text-[#78BE20] mx-auto mb-4" />
                <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mb-2">Application Approved</h1>
                <p className="text-muted-foreground mb-4">You've been approved for Trade pricing. Discounted prices are now visible across the site.</p>
                <Button onClick={() => navigate('/products')} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">Start Shopping</Button>
              </>
            )}
            {existingApp.status === 'rejected' && (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mb-2">Application Not Approved</h1>
                <p className="text-muted-foreground">Unfortunately, your trade application was not approved at this time. Please contact us at sales@techdistribution.com.au for more information.</p>
              </>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="trade-application-page">
        {/* Hero */}
        <div className="text-center mb-10">
          <Building2 className="h-12 w-12 text-[#78BE20] mx-auto mb-4" />
          <EditableText
            value="Trade Account Application"
            onChange={() => {}}
            tag="h1"
            className="text-3xl font-black text-foreground uppercase tracking-tight"
          />
          <EditableText
            value="Access exclusive trade pricing on our full product range. Applications are reviewed within 1-2 business days."
            onChange={() => {}}
            tag="p"
            className="text-muted-foreground mt-3 max-w-xl mx-auto"
          />
        </div>

        {/* Eligibility */}
        <div className="bg-card border border-[#78BE20]/30 rounded-sm p-6 mb-8" data-testid="trade-eligibility">
          <h2 className="text-lg font-bold text-foreground mb-3">Who is eligible?</h2>
          <p className="text-muted-foreground text-sm mb-4">
            Trade accounts are available to businesses operating in the following industries:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-start gap-3 p-3 bg-background rounded">
              <Wifi className="h-5 w-5 text-[#78BE20] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground text-sm font-medium">WiSP / ISP</p>
                <p className="text-muted-foreground text-xs">Wireless and internet service providers</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-background rounded">
              <Radio className="h-5 w-5 text-[#78BE20] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground text-sm font-medium">Telecommunications</p>
                <p className="text-muted-foreground text-xs">Telco contractors and carriers</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-background rounded">
              <Server className="h-5 w-5 text-[#78BE20] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-foreground text-sm font-medium">IT / MSP</p>
                <p className="text-muted-foreground text-xs">IT companies and managed service providers</p>
              </div>
            </div>
          </div>
          <p className="text-muted-foreground text-xs mt-3">
            Electrical, solar, security, and network integration businesses are also welcome to apply. A valid ABN is required.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-sm p-4 text-center">
            <div className="text-2xl font-black text-[#78BE20] mb-1">15-30%</div>
            <p className="text-sm text-muted-foreground">Off Retail Prices</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4 text-center">
            <div className="text-2xl font-black text-[#78BE20] mb-1">NET 30</div>
            <p className="text-sm text-muted-foreground">Payment Terms</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4 text-center">
            <div className="text-2xl font-black text-[#78BE20] mb-1">Priority</div>
            <p className="text-sm text-muted-foreground">Support & Shipping</p>
          </div>
        </div>

        {/* Login prompt for non-logged-in users */}
        {!user && (
          <div className="bg-card border border-yellow-500/30 rounded-sm p-5 mb-6 flex items-center justify-between">
            <div>
              <p className="text-foreground font-semibold">Ready to apply?</p>
              <p className="text-muted-foreground text-sm">Log in or create an account to submit your trade application.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-4">
              <Link to="/login"><Button variant="outline">Log In</Button></Link>
              <Link to="/register"><Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">Register</Button></Link>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-sm p-8 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-foreground mb-4">Business Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="business_name">Business Name *</Label>
                <Input id="business_name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 bg-background border-border" required data-testid="business-name-input" />
              </div>
              <div>
                <Label htmlFor="abn">ABN *</Label>
                <Input id="abn" value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} className="mt-1 bg-background border-border" required placeholder="XX XXX XXX XXX" data-testid="abn-input" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="industry">Industry *</Label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })} required>
                  <SelectTrigger className="mt-1 bg-background border-border" data-testid="industry-select">
                    <SelectValue placeholder="Select your industry..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-bold text-foreground mb-4">Contact Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input id="contact_name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 bg-background border-border" required data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-background border-border" required data-testid="trade-email-input" />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-background border-border" required data-testid="trade-phone-input" />
              </div>
              <div>
                <Label htmlFor="website">Website (Optional)</Label>
                <Input id="website" type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 bg-background border-border" placeholder="https://" data-testid="website-input" />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-bold text-foreground mb-4">Addresses</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="billing_address">Billing Address *</Label>
                <Textarea id="billing_address" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="mt-1 bg-background border-border" required rows={3} data-testid="billing-address-input" />
              </div>
              <div>
                <Label htmlFor="shipping_address">Shipping Address *</Label>
                <Textarea id="shipping_address" value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className="mt-1 bg-background border-border" required rows={3} data-testid="shipping-address-input" />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-background border-border" rows={3} placeholder="Tell us about your business, typical order volumes, products of interest, etc." data-testid="notes-input" />
          </div>

          <Button type="submit" disabled={loading || !form.industry} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12" data-testid="submit-application-btn">
            {loading ? 'Submitting...' : 'Submit Trade Application'}
          </Button>
        </form>
      </div>
      <CMSBlockRenderer excludeTypes={['hero']} />
      <AdminToolbar />
    </Layout>
  );
}
